import React from 'react';
import { useT } from '../i18n';
import { useNotificationSystem } from '../context/NotificationContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import './NotificationMenuItem.css';

// ── NOTIFICATIONS, IN THE USER MENU ──────────────────────────────────────────
// The one notification that is optional, as a row in the account dropdown.
//
// Sound, the header badge and the in-app toast are deliberately NOT switchable.
// They stay on for everyone: a message you never notice is the failure the whole
// system exists to prevent, and they are contained inside a page the user chose
// to have open. A push notification is different in kind — it escapes onto the
// lock screen — so it is opt-in, and it needs the browser's own permission
// besides.
//
// WHY THE TOGGLE DOES TWO THINGS. `setBrowserPush` covers a backgrounded TAB
// (the in-page Notification API, no server involved); `push.enable` registers
// this device with FCM so a CLOSED app still reaches the lock screen. Separate
// mechanisms, both wanted, one switch.
export default function NotificationMenuItem({ style }) {
  const { t } = useT();
  const { pushEnabled, permission, setBrowserPush } = useNotificationSystem();
  const push = usePushNotifications();

  const blocked = permission === 'denied';
  const unsupported = permission === 'unsupported';
  const off = blocked || unsupported;

  const on = !!pushEnabled && !off;

  // The permission prompt is only honoured inside a user gesture, so this runs
  // straight off the change event with nothing awaited before it.
  const toggle = async (next) => {
    if (off || push.busy) return;
    await setBrowserPush(next);
    if (next) await push.enable();
    else await push.disable();
  };

  // Why it cannot be switched on, when that is the case. Without this the
  // switch simply refuses to move and there is nothing on screen saying why.
  const hint = unsupported ? t('ns_unsupported')
    : blocked ? t('ns_blocked')
    : push.needsInstallFirst ? t('ns_ios_install')
    : (push.status && !push.status.ok) ? push.status.message
    : null;

  return (
    <div className="nmi">
      <label className={`nmi-row${off ? ' nmi-row--off' : ''}`} style={style}>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
          strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>

        <span className="nmi-label">{t('ns_title')}</span>

        {/* A real checkbox, visually hidden but still the thing being clicked
            and focused — keyboard and screen readers keep working; the track
            and knob are decoration painted on top. */}
        <span className="nmi-switch">
          <input
            type="checkbox"
            checked={on}
            onChange={(e) => toggle(e.target.checked)}
            disabled={off || push.busy}
            aria-label={t('ns_title')}
          />
          <span className="nmi-track" aria-hidden="true"><span className="nmi-knob" /></span>
        </span>
      </label>

      {hint && <p className="nmi-hint">{hint}</p>}
    </div>
  );
}
